import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import './PublicCandidateProfile.css';

function PublicCandidateProfile() {
  const { slug } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContact, setShowContact] = useState(false);

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/candidates/public/${slug}`);
        setCandidate(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.response?.data?.error || 'Profile not found');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  if (loading) {
    return (
      <div className="public-profile">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-profile">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Profile Not Found</h2>
          <p>{error}</p>
          <p className="error-subtext">This profile may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  const getStatusClass = (status) => {
    if (!status) return '';
    const lower = status.toLowerCase();
    if (lower.includes('complete') || lower.includes('pass')) return 'complete';
    if (lower.includes('pending')) return 'pending';
    if (lower.includes('fail') || lower.includes('positive')) return 'failed';
    return '';
  };

  return (
    <div className="public-profile">
      <div className="profile-header">
        <img src="/logo.png" alt="Mountain Recruiting" className="company-logo" />
        <h1>Candidate Profile</h1>
      </div>

      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-hero">
            <div className="profile-name-section">
              <h2 className="profile-name">{toTitleCase(candidate.candidate_name)}</h2>
              {candidate.company_name && (
                <p className="profile-company">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                  {candidate.company_name}
                </p>
              )}
            </div>
          </div>

          <div className="profile-section">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Professional Information
            </h3>

            <div className="info-grid">
              {candidate.location && (
                <div className="info-item">
                  <span className="info-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    Location:
                  </span>
                  <span className="info-value">{candidate.location}</span>
                </div>
              )}

              {candidate.last_worked && (
                <div className="info-item">
                  <span className="info-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                    Experience:
                  </span>
                  <span className="info-value">{candidate.last_worked}</span>
                </div>
              )}

              {candidate.experience_summary && (
                <div className="info-item full-width">
                  <span className="info-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    Summary:
                  </span>
                  <span className="info-value">{candidate.experience_summary}</span>
                </div>
              )}
            </div>

            {candidate.indeed_url && (
              <div className="resume-section">
                <a
                  href={candidate.indeed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resume-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  View Resume
                </a>
              </div>
            )}
          </div>

          {(candidate.bg_status || candidate.drug_status || candidate.physical_status || candidate.gcic_status) && (
            <div className="profile-section">
              <h3 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Status Checks
              </h3>

              <div className="status-grid">
                {candidate.bg_status && (
                  <div className="status-item">
                    <span className="status-label">Background Check:</span>
                    <span className={`status-badge ${getStatusClass(candidate.bg_status)}`}>
                      {candidate.bg_status}
                    </span>
                  </div>
                )}

                {candidate.drug_status && candidate.drug_status !== 'Status' && (
                  <div className="status-item">
                    <span className="status-label">Drug Test:</span>
                    <span className={`status-badge ${getStatusClass(candidate.drug_status)}`}>
                      {candidate.drug_status}
                    </span>
                  </div>
                )}

                {candidate.physical_status && candidate.physical_status !== 'Status' && (
                  <div className="status-item">
                    <span className="status-label">Physical:</span>
                    <span className={`status-badge ${getStatusClass(candidate.physical_status)}`}>
                      {candidate.physical_status}
                    </span>
                  </div>
                )}

                {candidate.gcic_status && (
                  <div className="status-item">
                    <span className="status-label">GCIC:</span>
                    <span className={`status-badge ${getStatusClass(candidate.gcic_status)}`}>
                      {candidate.gcic_status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="profile-section contact-section">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              Contact Information
            </h3>

            {!showContact ? (
              <button
                className="show-contact-btn"
                onClick={() => setShowContact(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Show Contact Information
              </button>
            ) : (
              <div className="contact-info-revealed">
                <div className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <div className="contact-details">
                    <span className="contact-label">Phone:</span>
                    <a href={`tel:${candidate.phone}`} className="contact-value">{candidate.phone}</a>
                  </div>
                </div>

                <div className="contact-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <div className="contact-details">
                    <span className="contact-label">Email:</span>
                    <a href={`mailto:${candidate.email}`} className="contact-value">{candidate.email}</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="profile-footer">
          <p>Powered by <strong>Mountain Recruiting</strong></p>
        </footer>
      </div>
    </div>
  );
}

export default PublicCandidateProfile;
